import React, { useMemo, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import clsx from 'clsx';
import { Music24PlanTier, music24PlanCapabilities } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../../../common/types';
import { BillingInterval, MUSIC24_PLANS, yearlyToMonthly } from '../../../config/music24StripePlans';
import { planNameMessages } from '../../../config/music24PlanNames';
import { formatPlanPrice, useDisplayCurrency } from '../../../config/useDisplayCurrency';
import {
  MUSIC24_FEATURE_GROUPS,
  featureGroupMessages,
  featureRowNoneText,
  featureRowsInGroup,
} from '../../../config/music24PlanFeatureRows';
import { useMusic24Plan } from '../../../hooks/useMusic24Plan';
import { useIsNativeEmbed } from '../../../navigation/hooks/useIsNativeEmbed';
import { PLANS_COMPARE_ID } from '../../../plans/scrollToPlansCompare';
import { CompareTier, CompareTierSwitch } from './CompareTierSwitch';
import { CompareGroup, CompareGroupData } from './CompareGroup';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the compare table; each part uses its own slice of it */
import styles from './PricingCompareTable.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The columns — `trial` is not sold, so it has no column. Plug is not sold any more (owner,
 * 2026-10-23): its column shows only to a workspace that is still on Plug (`compareTiers`).
 */
export const COMPARE_TIERS: CompareTier[] = ['free', 'artist', 'pro', 'plug', 'manager', 'label'];

/** The columns that the signed-in workspace sees. */
export function compareTiers(onPlug: boolean): CompareTier[] {
  return COMPARE_TIERS.filter((tier) => tier !== 'plug' || onPlug);
}

/** The column that is always on a phone, and tinted on every width (design plans-page a). */
export const COMPARE_ANCHOR_TIER: Music24PlanTier = MUSIC24_PLANS.find((plan) => plan.mostPopular)?.tier ?? 'pro';

/** The plan that a phone compares with Pro until the reader picks another one. */
const DEFAULT_PAIR_TIER: CompareTier = 'artist';

const messages = defineMessages({
  heading: { id: 'app.sml.pricing.compare.heading', defaultMessage: 'Compare plans' },
  perMonth: { id: 'app.sml.plans.compare.perMonth', defaultMessage: '{price} / mo' },
});

export interface PricingCompareTableProps {
  interval: BillingInterval;
}

/**
 * Design plans-page a: the full compare table under the plan cards. It has no numbers of its own:
 * every cell is `MUSIC24_FEATURE_ROWS` read against the plan's capabilities. The head row stays
 * on screen while the page scrolls. Below 760 px only two columns show: Pro and the plan of the
 * switch above the table.
 */
const PricingCompareTableComponent: React.FC<PricingCompareTableProps> = ({ interval }) => {
  const intl = useIntl();
  const currency = useDisplayCurrency();
  const isNativeEmbed = useIsNativeEmbed();
  const onPlug = useMusic24Plan() === 'plug';
  const tiers = useMemo(() => compareTiers(onPlug), [onPlug]);
  const pairOptions = useMemo(() => tiers.filter((tier) => tier !== COMPARE_ANCHOR_TIER), [tiers]);
  const [pairTier, setPairTier] = useState<CompareTier>(DEFAULT_PAIR_TIER);

  const columns = useMemo(
    () =>
      tiers.map((tier) => {
        const plan = MUSIC24_PLANS.find((candidate) => candidate.tier === tier);
        const cents =
          plan === undefined
            ? 0
            : interval === 'yearly'
              ? yearlyToMonthly(plan.prices.yearly.amount)
              : plan.prices.monthly.amount;
        return {
          tier,
          name: intl.formatMessage(planNameMessages[tier]),
          price:
            plan === undefined
              ? formatPlanPrice(intl, 0, currency)
              : intl.formatMessage(messages.perMonth, { price: formatPlanPrice(intl, cents, currency) }),
          className: clsx(
            tier === COMPARE_ANCHOR_TIER && styles.highlight,
            tier !== COMPARE_ANCHOR_TIER && tier !== pairTier && styles.wideOnly
          ),
        };
      }),
    [tiers, interval, intl, currency, pairTier]
  );

  const groups = useMemo((): CompareGroupData[] => {
    const none = featureRowNoneText(intl);
    return MUSIC24_FEATURE_GROUPS.map((group) => ({
      group,
      label: intl.formatMessage(featureGroupMessages[group]),
      rows: featureRowsInGroup(group).map((row) => ({
        key: row.key,
        label: intl.formatMessage(row.label),
        cells: tiers.map((tier) => {
          const text = row.value(music24PlanCapabilities[tier], intl);
          return { tier, text, isNone: text === none };
        }),
      })),
    }));
  }, [intl, tiers]);

  const classNameOf = useMemo(() => new Map(columns.map((column) => [column.tier, column.className])), [columns]);

  return (
    <section className={styles.section}>
      <h2 className={styles.heading}>{intl.formatMessage(messages.heading)}</h2>
      <CompareTierSwitch options={pairOptions} value={pairTier} onChange={setPairTier} />
      <div className={styles.wrap} id={PLANS_COMPARE_ID}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.first} aria-label={intl.formatMessage(messages.heading)} />
              {columns.map((column) => (
                <th key={column.tier} className={column.className}>
                  <div className={styles.planName}>{column.name}</div>
                  {!isNativeEmbed && <div className={styles.planPrice}>{column.price}</div>}
                </th>
              ))}
            </tr>
          </thead>
          {groups.map((group, index) => (
            <CompareGroup
              key={group.group}
              data={group}
              foldable={index > 0}
              columnCount={columns.length + 1}
              classNameOf={classNameOf}
            />
          ))}
        </table>
      </div>
    </section>
  );
};

export const PricingCompareTable = TypedMemo(PricingCompareTableComponent);
